import type { QuoteDetailDto } from "@/lib/quotes";
import { QuoteCustomerDetailsPanel } from "./QuoteCustomerDetailsPanel";
import { QuoteRefDescCard } from "./QuoteRefDescCard";
import { QuoteOrderDetailsCard } from "./QuoteOrderDetailsCard";
import { QuoteOrderItemsCard } from "./QuoteOrderItemsCard";
import { QuoteCostsCard } from "./QuoteCostsCard";
import { QuoteStatusTermsCard } from "./QuoteStatusTermsCard";
import { QuoteCommentsCard } from "./QuoteCommentsCard";

export interface QuoteTabProps {
  quote: QuoteDetailDto;
  onAddFromProduct: () => void;
  onAddManufacturing?: () => void;
  onEditManufacturing?: (mfgBomId: number) => void;
  onSetManualTotals?: (totalExGst: number | null) => void;
}

/**
 * Quote tab body — 3-column grid. The action toolbar now lives in the
 * DetailPage `tabActions` slot (rendered next to the tab bar), not inside
 * this component.
 *
 *   Left  (col-span 3) : Customer Details sidebar (full-height stack)
 *   Mid   (col-span 5) : Customer Ref + Quote Desc / Order Details /
 *                        Order Items / Comments
 *   Right (col-span 4) : Costs / Quote Status + T&Cs
 *
 * Stacks to single column below lg. Customer Details is col-span-3 (3/12) so
 * the title and labels fit comfortably without wrapping.
 */
export function QuoteTab({
  quote,
  onAddFromProduct,
  onAddManufacturing,
  onEditManufacturing,
  onSetManualTotals,
}: QuoteTabProps) {
  const editable = ["Draft", "Sent"].includes(quote.status);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <aside className="lg:col-span-3 flex flex-col">
          <QuoteCustomerDetailsPanel quote={quote} />
        </aside>

        <div className="flex flex-col gap-4 lg:col-span-5">
          <QuoteRefDescCard quote={quote} editable={editable} />
          <QuoteOrderDetailsCard quote={quote} onAddFromProduct={onAddFromProduct} onAddManufacturing={onAddManufacturing} onEditManufacturing={onEditManufacturing} />
          <QuoteOrderItemsCard quoteId={quote.quoteId} editable={editable} />
          <QuoteCommentsCard quote={quote} />
        </div>

        <div className="flex flex-col gap-4 lg:col-span-4">
          <QuoteCostsCard
            quote={quote}
            editable={editable}
            onSetManualTotals={onSetManualTotals}
          />
          <QuoteStatusTermsCard quote={quote} editable={editable} />
        </div>
      </div>
    </div>
  );
}
